import DatePicker from "@components/common/DatePicker";
import ListTable from "@components/common/ListTable";
import Pagination from "@components/common/pagination";
import { useIsTablet } from "@components/mediaQuery/useIsMobile";
import { fetchKitHistoryData } from "@services/vesselService";
import { setLoadingkitHistory } from "@store/modules/vesselsSlice";
import { isInternalUser } from "@utils/isInternalUser";
import { kitHistoryOperationsTypes } from "@utils/kit_history_types";
import { Modal } from "flowbite-react";
import { hasDistributorAdminRole } from "helpers/rolesHelpers";
import { debounce } from "lodash";
import { startTransition, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { customModalTheme } from "styles/customTheme";
import { ModalKitHistoryDetails } from "./ModalKitHistoryDetails";

export const ModalKitHistory = ({ id, isOpen, kitNumber, title, closeModal }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { token, permissions, role } = useSelector((state) => state.auth.user);

	const [selectedActivity, setSelectedActivity] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const [errors, setErrors] = useState({ fromDate: "", toDate: "" });

	const [searchTerms, setSearchTerms] = useState({
		fromDate: "",
		toDate: "",
	});

	const handleSearchChange = useCallback((id, value) => {
		setSearchTerms((prevTerms) => ({ ...prevTerms, [id]: value }));
	}, []);

	const {
		loading = false,
		data: timelineData = [],
		pagination: { totalItems = 0, totalPage = 1, page = 1, perPage = 10 } = {},
	} = useSelector((state) => state.modules.vessels.activation?.kits?.history || {});
	const [currentPage, setCurrentPage] = useState(1);

	const canViewKitHistoryDetails = isInternalUser(role) || hasDistributorAdminRole(role);

	const loadkitHistory = async (signal) => {
		try {
			dispatch(setLoadingkitHistory(true));

			await fetchKitHistoryData(
				id,
				token,
				dispatch,
				currentPage, // page
				perPage, // perPage
				searchTerms.fromDate || null,
				searchTerms.toDate || null,
				signal,
			);
		} catch (error) {
			console.error("Failed to load kit history:", error);
		} finally {
			dispatch(setLoadingkitHistory(false));
		}
	};

	const debouncedkitHistory = useMemo(
		() =>
			debounce((signal) => {
				loadkitHistory(signal);
			}, 300),
		[token, id, searchTerms, currentPage, perPage],
	);

	useEffect(() => {
		const controller = new AbortController();

		debouncedkitHistory(controller.signal);

		return () => {
			debouncedkitHistory.cancel();
			controller.abort();
		};
	}, [debouncedkitHistory, currentPage]);

	const handleDateChange = (date, field) => {
		if (!date) return;
		const selectedDate = new Date(date);

		const year = selectedDate.getFullYear();
		const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
		const day = String(selectedDate.getDate()).padStart(2, "0");

		const formattedDate = `${year}-${month}-${day}`;

		const updatedErrors = { ...errors };
		const updatedSearchTerms = { ...searchTerms, [field]: formattedDate };

		if (field === "fromDate") {
			if (searchTerms?.toDate && selectedDate > new Date(searchTerms.toDate)) {
				updatedErrors.fromDate = "From date cannot be later than To date.";
			} else {
				updatedErrors.fromDate = "";
				updatedErrors.toDate = "";
			}
		}
		if (field === "toDate") {
			if (searchTerms?.fromDate && selectedDate < new Date(searchTerms.fromDate) + 1) {
				updatedErrors.toDate = "To date cannot be earlier than From date.";
			} else {
				updatedErrors.toDate = "";
				updatedErrors.fromDate = "";
			}
		}

		setErrors(updatedErrors);
		setSearchTerms(updatedSearchTerms);
		setCurrentPage(1);
	};

	const handleClearFromDateSearch = (id) => {
		startTransition(() => {
			setSearchTerms((prevTerms) => ({
				...prevTerms,
				fromDate: "",
			}));
		});
		setErrors((prevErrors) => ({
			...prevErrors,
			fromDate: "",
		}));
	};
	const handleClearToDateSearch = (id) => {
		startTransition(() => {
			setSearchTerms((prevTerms) => ({
				...prevTerms,

				toDate: "",
			}));

			setErrors((prevErrors) => ({
				...prevErrors,
				toDate: "",
			}));
		});
	};

	const columns = [
		{
			header: "Operation",
			field: "type",
			content: (rowData) => {
				const activity = kitHistoryOperationsTypes?.find((item) => item.value === rowData.type);

				return activity?.label || "-";
			},
		},
		{
			header: "Subject",
			field: "subject",
			content: (rowData) => {
				const subject = rowData?.json_data?.subject || "";

				return (
					<div className="group relative max-w-[250px] cursor-pointer">
						<p className="text-[10px] sm:text-xs">{subject || "-"}</p>
					</div>
				);
			},
		},
		{
			header: "Date",
			field: "date",
			content: (rowData) => {
				const creationDateString = rowData.created_at?.split(".")[0];
				const date = new Date(creationDateString);

				if (isNaN(date.getTime())) {
					return "Invalid Date";
				}

				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, "0");
				const day = String(date.getDate()).padStart(2, "0");
				const hours = String(date.getHours()).padStart(2, "0");
				const minutes = String(date.getMinutes()).padStart(2, "0");
				const seconds = String(date.getSeconds()).padStart(2, "0");

				const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

				return formattedDate;
			},
		},
	];

	const isTablet = useIsTablet();

	const handleViewDetails = (row) => {
		setSelectedActivity(row);
		setIsModalOpen(true);
	};

	const startItemIndex = (currentPage - 1) * perPage + 1;
	const endItemIndex = Math.min(currentPage * perPage, totalItems);

	const handlePageChange = (newPage) => {
		if (newPage !== currentPage && newPage >= 1 && newPage <= totalPage) {
			setCurrentPage(newPage);
		}
	};

	return (
		<Modal
			show={isOpen}
			onClose={closeModal}
			id="historykit"
			className="h-[90dvh]"
			size="7xl"
			theme={customModalTheme}
		>
			<Modal.Header>
				<div className="flex items-center gap-3">
					<div>
						<h2 className="text-blue-gray text-sm capitalize md:text-xl">{kitNumber}</h2>
					</div>
				</div>
			</Modal.Header>
			<Modal.Body className="custom-scrollbar bg-navbar-bg">
				<div className="h-[60dvh] w-full bg-navbar-bg p-4 shadow md:rounded-lg">
					<form className="mt-4">
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
							<div>
								<label htmlFor="fromDate" className="mb-2 block text-left text-gray-400 text-xs">
									From Date
								</label>
								<DatePicker
									id="fromDate"
									placeholder="Select from date"
									value={searchTerms?.fromDate}
									onChange={(date) => handleDateChange(date, "fromDate")}
									onClear={handleClearFromDateSearch}
									weekStart={1}
									maxDate={searchTerms?.toDate ? new Date(searchTerms.toDate) : undefined}
									positionDatePicker={`${isTablet ? "left-0" : "right-0 "} absolute block z-50 top-full mt-2`}
								/>
								{errors.fromDate && (
									<p className="mt-1 text-start text-red-500 text-xs">{errors.fromDate}</p>
								)}
							</div>
							<div>
								<label htmlFor="toDate" className="mb-2 block text-left text-gray-400 text-xs">
									To Date
								</label>
								<DatePicker
									id="toDate"
									placeholder="Select to date"
									value={searchTerms?.toDate}
									onChange={(date) => handleDateChange(date, "toDate")}
									onClear={handleClearToDateSearch}
									weekStart={1}
									minDate={searchTerms?.fromDate ? new Date(searchTerms.fromDate) : undefined}
									maxDate={new Date()}
									positionDatePicker={`${isTablet ? "right-0" : "left-0 "} absolute block z-50 top-full mt-2`}
								/>
								{errors.toDate && (
									<p className="mt-1 text-start text-red-500 text-xs">{errors.toDate}</p>
								)}
							</div>
						</div>
					</form>

					{loading ? (
						<div className="flex flex-col items-center justify-center py-16 text-white">
							<svg
								className="mb-4 h-10 w-10 animate-spin text-blue-400"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								></circle>
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
							</svg>
							<p className="animate-pulse text-gray-300 text-xs">Loading activity history...</p>
						</div>
					) : timelineData ? (
						<div className="py-10 text-white">
							<ListTable
								list={timelineData}
								columns={columns}
								viewColumnData={canViewKitHistoryDetails}
								handleViewDetails={(row) => handleViewDetails(row)}
							/>
							<div className="flex flex-col items-center justify-between gap-2 bg-dark-blue bg-opacity-40 px-2 pt-4 pb-4 text-blue-gray text-xs sm:flex-row sm:gap-0 sm:px-4 sm:text-sm">
								<span>
									Showing {startItemIndex} to {endItemIndex} of {totalItems}
								</span>

								<Pagination
									currentPage={currentPage}
									totalPages={totalPage}
									onPageChange={handlePageChange}
								/>
							</div>
						</div>
					) : (
						<p className="py-10 text-center text-gray-400">No data.</p>
					)}
				</div>
			</Modal.Body>

			{isModalOpen && (
				<ModalKitHistoryDetails
					index={selectedActivity?.id}
					isOpenDetails={isModalOpen}
					closeModal={() => setIsModalOpen(false)}
					activity={selectedActivity}
				/>
			)}
		</Modal>
	);
};
